// Phase 30 — Fahrer Aufgaben (driver tasks list & complete)
import { useEffect, useState } from "react";
import { fahrerApi } from "./lib/api";
import { Truck, CheckCircle2, AlertCircle, X, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";

const FILTERS = [
  { k: "open", label: "Offen" },
  { k: "all", label: "Alle" },
  { k: "done", label: "Erledigt" },
];

export default function FahrerAufgaben() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await fahrerApi.get("/fahrer/tasks");
      setItems(data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const visible = items.filter((t) => {
    if (filter === "open") return t.status !== "erledigt" && t.status !== "abgelehnt";
    if (filter === "done") return t.status === "erledigt";
    return true;
  });

  async function markDone(id) {
    try { await fahrerApi.post(`/fahrer/tasks/${id}/complete`); toast.success("Erledigt"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  return (
    <div className="space-y-4" data-testid="fahrer-aufgaben-page">
      <div>
        <h1 className="text-xl font-bold text-zinc-50 flex items-center gap-2">
          <Truck className="w-5 h-5 text-amber-400" /> Aufgaben
        </h1>
        <p className="text-xs text-zinc-500 mt-0.5">Aufgaben vom Büro / Disposition</p>
      </div>

      <HelpBox title="Wie funktioniert das?">
        <p>Hier siehst du alle Aufgaben, die das Büro dir zugewiesen hat.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-1">
          <li>Neue Aufgaben blockieren das Portal, bis du <b>Annehmen</b> oder <b>Ablehnen</b> klickst.</li>
          <li>Bei Ablehnung musst du einen Grund angeben (mind. 5 Zeichen).</li>
          <li>Angenommene Aufgaben kannst du als <b>Erledigt</b> markieren, sobald du sie abgeschlossen hast.</li>
        </ul>
      </HelpBox>

      <div className="flex gap-1.5">
        {FILTERS.map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`fahrer-task-filter-${f.k}`}
                  className={`text-xs px-3 py-1.5 rounded font-medium ${filter === f.k ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-300"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 text-sm py-8">Lädt…</div>
      ) : visible.length === 0 ? (
        <div className="text-center text-zinc-500 text-xs italic py-6 bg-zinc-900 border border-zinc-800 rounded-lg">
          Keine Aufgaben in dieser Ansicht.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((t) => <DriverTaskCard key={t.id} task={t} onComplete={() => markDone(t.id)} />)}
        </div>
      )}
    </div>
  );
}

function DriverTaskCard({ task, onComplete }) {
  const t = task;
  const isPending = t.driver_response === "pending";
  const isAccepted = t.driver_response === "accepted";
  const isRejected = t.driver_response === "rejected";
  const isDone = t.status === "erledigt";

  return (
    <div className={`p-3 rounded-lg border ${
      isDone ? "bg-zinc-900/40 border-zinc-800 opacity-70"
      : isRejected ? "bg-red-500/5 border-red-500/30"
      : isPending ? "bg-amber-500/5 border-amber-500/40"
      : "bg-zinc-900 border-zinc-800"}`} data-testid={`fahrer-task-card-${t.id}`}>
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 flex items-center justify-center shrink-0">
          <Truck className="w-3.5 h-3.5"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-1.5">
            <h3 className={`text-sm font-semibold ${isDone ? "line-through text-zinc-500" : "text-zinc-100"}`}>{t.title}</h3>
            {isPending && <Badge tone="amber" icon={AlertCircle}>Wartet auf Annahme</Badge>}
            {isAccepted && !isDone && <Badge tone="emerald" icon={Check}>Angenommen</Badge>}
            {isRejected && <Badge tone="red" icon={X}>Abgelehnt</Badge>}
            {isDone && <Badge tone="zinc" icon={CheckCircle2}>Erledigt</Badge>}
            <Badge tone="zinc" icon={Clock}>{t.priority}</Badge>
          </div>
          {t.description && <div className="text-xs text-zinc-400 mt-1">{t.description}</div>}
          {t.notes && <div className="text-[11px] text-amber-200/90 italic mt-1 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1">Hinweis: {t.notes}</div>}
          <div className="text-[10px] text-zinc-500 mt-1">
            Vergeben von <b className="text-zinc-300">{t.created_by || "—"}</b>
            {" · "}{fmtDateTime(t.created_at)}
            {t.due_date && <> · Fällig <span className="text-zinc-300">{t.due_date}{t.due_time ? ` ${t.due_time}` : ""}</span></>}
          </div>
          {isRejected && t.driver_response_reason && (
            <div className="text-[11px] text-red-300 italic mt-1">Dein Grund: „{t.driver_response_reason}"</div>
          )}
          {isDone && t.done_at && (
            <div className="text-[10px] text-emerald-300 mt-1">Erledigt am {fmtDateTime(t.done_at)}</div>
          )}
        </div>
      </div>
      {isAccepted && !isDone && (
        <button onClick={onComplete} data-testid={`fahrer-task-complete-${t.id}`}
                className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold">
          <Check className="w-3.5 h-3.5"/> Als erledigt markieren
        </button>
      )}
    </div>
  );
}

function Badge({ tone, icon: Icon, children }) {
  const map = {
    amber: "bg-amber-500/20 text-amber-200 border-amber-500/40",
    emerald: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
    red: "bg-red-500/20 text-red-200 border-red-500/40",
    zinc: "bg-zinc-700/60 text-zinc-300 border-zinc-600",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${map[tone]}`}>
      {Icon && <Icon className="w-3 h-3"/>}{children}
    </span>
  );
}

function HelpBox({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg" data-testid="fahrer-aufgaben-help">
      <button type="button" onClick={() => setOpen(v => !v)}
              className="w-full px-3 py-2 text-left flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-amber-300">ℹ {title}</span>
        <span className="text-[10px] text-amber-300/80">{open ? "Schließen" : "Öffnen"}</span>
      </button>
      {open && <div className="px-3 pb-3 text-[11px] text-zinc-200 space-y-1.5">{children}</div>}
    </div>
  );
}
